import type { User } from '@/types'
import { normalizeRole } from './access'

export const SESSION_EXPIRED_EVENT = 'cqu:session-expired'
interface Session { token: string | null; user: User | null }

function normalizeUser(value: unknown): User | null {
  if (!value || typeof value !== 'object') return null
  const user = value as User
  const role = normalizeRole(user.role)
  if (!role || !Number.isInteger(user.id) || user.id <= 0 || typeof user.name !== 'string') return null
  return { ...user, role }
}

function restore(): Session {
  try {
    const token = localStorage.getItem('token')
    const user = normalizeUser(JSON.parse(localStorage.getItem('user') || 'null'))
    return { token: token || null, user }
  } catch {
    return { token: null, user: null }
  }
}

// The same in-memory session backs Pinia and requests. Storage failures cannot
// leave one using a new token and the other an older one.
let session = restore()
const listeners = new Set<(value: Session) => void>()
export const currentSession = () => session
export const sessionToken = () => session.token
export function subscribeSession(listener: (value: Session) => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
function persist(next: Session) {
  session = next
  try {
    if (next.token) localStorage.setItem('token', next.token)
    else localStorage.removeItem('token')
    if (next.user) localStorage.setItem('user', JSON.stringify(next.user))
    else localStorage.removeItem('user')
  } catch { /* A WebView may deny storage; the current session still works. */ }
  for (const listener of listeners) listener(session)
}
export function saveSession(token: string, value: unknown) {
  const user = normalizeUser(value)
  if (typeof token !== 'string' || !token || !user) throw new Error('登录身份信息无效，请重新登录。')
  persist({ token, user })
}
export function updateSessionUser(value: unknown, expectedToken: string, expectedUser?: User | null) {
  if (session.token !== expectedToken || (expectedUser !== undefined && session.user !== expectedUser)) return
  const user = normalizeUser(value)
  if (!user) throw new Error('服务器返回了无法识别的用户身份。')
  persist({ token: expectedToken, user })
}
export function clearSession() { persist({ token: null, user: null }) }
export function expireSession(expectedToken: string) {
  if (!expectedToken || session.token !== expectedToken) return
  clearSession()
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
}

// Keep other tabs in sync without writing back and causing storage-event loops.
function storageChanged(event: StorageEvent) {
  if (event.key !== null && event.key !== 'token' && event.key !== 'user') return
  session = restore()
  for (const listener of listeners) listener(session)
}
if (typeof window !== 'undefined') window.addEventListener('storage', storageChanged)
if (import.meta.hot) import.meta.hot.dispose(() => window.removeEventListener('storage', storageChanged))
